/* eslint-disable react-hooks/exhaustive-deps */

import {
  useState,
  useMemo,
  useRef,
  useEffect,
  useCallback,
  useContext,
} from "react";
import {
  MapContainer,
  TileLayer,
  Marker,
  Popup,
  useMap,
  useMapEvents,
} from "react-leaflet";
import { useConfig } from "../../context/configContext";
import { ChargerContext } from "../../context/ChargerContext";
import { getChargerProfile } from "../../services/chargerApi";
import { useNavigate } from "react-router-dom";
import api from "../interceptors.js";
import L from "leaflet";
import { BatteryCharging, MapPin, PlugZap, X, Zap } from "lucide-react";
import "leaflet/dist/leaflet.css";
import "./index.css";
/* ===========================
   Marker Icons
=========================== */
const DEFAULT_VIEW = {
  lat: 20.5937,
  lng: 78.9629,
  zoom: 5,
};

const greenIcon = new L.Icon({
  iconUrl:
    "https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-green.png",
  shadowUrl:
    "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.3.4/images/marker-shadow.png",
  iconSize: [25, 41],
  iconAnchor: [12, 41],
});

const redIcon = new L.Icon({
  iconUrl:
    "https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-red.png",
  shadowUrl:
    "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.3.4/images/marker-shadow.png",
  iconSize: [25, 41],
  iconAnchor: [12, 41],
});

/* ===========================
   Map Bounds Tracker
=========================== */
const MapBoundsTracker = ({ onBoundsChange, setMapView }) => {
  useMapEvents({
    moveend: (e) => {
      const map = e.target;
      if (!map) return;
      const bounds = map.getBounds();
      const center = map.getCenter();
      const zoom = map.getZoom();

      const newView = {
        lat: center.lat,
        lng: center.lng,
        zoom,
      };

      localStorage.setItem("mapView", JSON.stringify(newView));

      setMapView((prev) => {
        if (
          prev.lat === newView.lat &&
          prev.lng === newView.lng &&
          prev.zoom === newView.zoom
        ) {
          return prev;
        }
        return newView;
      });

      onBoundsChange({
        southWest: bounds.getSouthWest(),
        northEast: bounds.getNorthEast(),
        zoom,
      });
    },
  });

  return null;
};

const RecenterMap = ({ lat, lng, zoom }) => {
  const map = useMap();

  useEffect(() => {
    const nextLat = Number(lat);
    const nextLng = Number(lng);

    if (!Number.isFinite(nextLat) || !Number.isFinite(nextLng)) return;

    map.setView([nextLat, nextLng], zoom, { animate: true });
  }, [map, lat, lng, zoom]);

  return null;
};

/* ===========================
   Main Component
=========================== */
const MapView = ({
  searchTerm,
  searchResults,
  setSearchTerm,
  selectedLocation,
}) => {
  const [mapStations, setMapStations] = useState([]);
  const [selectedStation, setSelectedStation] = useState(null); // mobile
  const [errorMessage, setErrorMessage] = useState("");
  const { config, updateConfig } = useConfig();
  const { setChargerData } = useContext(ChargerContext);
  const navigate = useNavigate();
  const popupRef = useRef(null);
  const [mapView, setMapView] = useState(DEFAULT_VIEW);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    try {
      const savedView = localStorage.getItem("mapView");

      if (savedView) {
        const parsedView = JSON.parse(savedView);

        if (
          parsedView?.lat !== undefined &&
          parsedView?.lng !== undefined &&
          parsedView?.zoom !== undefined
        ) {
          setMapView(parsedView);
          return;
        }
      }
    } catch (err) {
      console.log("mapView parse error", err);
    }

    // fallback
    setMapView(DEFAULT_VIEW);
  }, []);

  /* ===========================
     Fetch Stations in Bounds
  =========================== */

  const fetchStationsInView = useCallback(
    async ({ southWest, northEast, zoom }) => {
      try {
        const response = await api.post("/api/map_bounds", {
          southLat: southWest.lat,
          southLng: southWest.lng,
          northLat: northEast.lat,
          northLng: northEast.lng,
          zoom,
        });

        const result = response.data;
        console.log(result);
        if (result.status === "success") {
          setMapStations(result.stations || []);
        }
      } catch (err) {
        console.error("Map fetch failed:", err);
      }
    },
    [],
  );

  /* ===========================
     Initial Load
  =========================== */
  useEffect(() => {
    fetchStationsInView({
      southWest: { lat: 5, lng: 68 },
      northEast: { lat: 37, lng: 97 },
      zoom: 5,
    });
  }, [fetchStationsInView]);

  useEffect(() => {
    if (selectedLocation) {
      setMapView({
        lat: Number(selectedLocation.latitude),
        lng: Number(selectedLocation.longitude),
        zoom: 15,
      });
    }
  }, [selectedLocation]);

  useEffect(() => {
    console.log("Config updated:", config.status);
  }, [config]);

  /* ===========================
     Normalize Locations
  =========================== */
  const LOCATIONS = useMemo(
    () =>
      mapStations.map((s) => {
        const normalizedStatus =
          s.status?.toUpperCase() === "ONLINE" ? "ONLINE" : "OFFLINE";

        return {
          id: s.station_id,
          name: s.charger_name,
          lat: Number(s.latitude),
          lng: Number(s.longitude),
          customer_id: s.customer_id,
          status: normalizedStatus,
          original: s,
          icon: normalizedStatus === "ONLINE" ? greenIcon : redIcon,
        };
      }),
    [mapStations],
  );

  // const calculateStatsFromProfile = (profile) => {
  //   const totalDispensers = profile.dispensers.length;

  //   const totalGuns = profile.dispensers.reduce(
  //     (sum, d) => sum + (d.guns?.length || 0),
  //     0
  //   );

  //   return { totalDispensers, totalGuns };
  // };

  /* ===========================
     Final Commit (API + Context)
  =========================== */

  const commitSelection = async (station) => {
    try {
      const response = await getChargerProfile({
        customer_id: station.customer_id,
        station_id: station.station_id,
      });

      if (!response.success) return;

      const profile = response.data;

      // Save full profile globally
      setChargerData(profile);
      console.log(profile.station_id);
      // Calculate stats from API response
      // const statusResponse = await fetch(
      //   `/api/get_online_status?station_id=${profile.station_id}`,
      // );

      // const statusData = await statusResponse.json();
      // setonlineStatus(
      //   statusData.dispensers.map((d) => ({
      //     [d.dispenser_id]: d.status,
      //   })),
      // );

      const totalDispensers = profile.dispensers.length;
      const totalGuns = profile.dispensers.reduce(
        (sum, d) => sum + (d.guns?.length || 0),
        0,
      );
      const resolvedStatus = station.status === "ONLINE" ? "ONLINE" : "OFFLINE";

      // Correct config update
      updateConfig({
        dispensers: totalDispensers,
        guns: totalGuns,
        station_id: profile.station_id,
        station_name: profile.station_name,
        customer_id: profile.customer_id,
        cpo_name: profile.cpo_name,
        dispensersData: profile.dispensers,
        status: resolvedStatus,
      });

      navigate("/");
    } catch (err) {
      setSelectedStation(null);
      if (err.response?.status === 400) {
        setErrorMessage("Please retry after a short while.");
      } else {
        setErrorMessage(
          <>
            Oops! Something went wrong. Please try again in a moment. <br />
            Refresh the page after a few seconds.
          </>,
        );
      }

      console.error("Charger profile error:", err);
    }
  };

  // const getMatchedField = (loc, searchTerm) => {
  //   const term = searchTerm.toLowerCase();

  //   if (loc.station_id?.toLowerCase().includes(term)) {
  //     return loc.station_id;
  //   }
  //   if (loc.address?.toLowerCase().includes(term)) {
  //     return loc.address;
  //   }
  //   if (loc.charger_name?.toLowerCase().includes(term)) {
  //     return loc.charger_name;
  //   }
  //   if (loc.customer_id?.toLowerCase().includes(term)) {
  //     return loc.customer_id;
  //   }

  //   return loc.station_id; // fallback
  // };
  /* ===========================
     Render
  =========================== */

  return (
    <div className="map-view-shell relative flex min-w-0 max-w-full flex-1 overflow-hidden">
      {/* Top Controls */}
      {/* Map */}
      <MapContainer
        center={[mapView.lat, mapView.lng]}
        zoom={mapView.zoom}
        scrollWheelZoom
        paddingBottomRight={[0, 200]}
        style={{
          height: "100%",
          maxWidth: "100%",
          width: "100%",
        }}
      >
        <MapBoundsTracker
          onBoundsChange={fetchStationsInView}
          setMapView={setMapView}
        />
        <RecenterMap lat={mapView.lat} lng={mapView.lng} zoom={mapView.zoom} />

        <TileLayer
          attribution="&copy; OpenStreetMap contributors"
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />

        {LOCATIONS.map((loc) => {
          const stats = {
            totalDispensors: loc.original.dispensers_count ?? 0,
            totalGuns: loc.original.guns_count ?? 0,
          };

          return (
            <Marker
              key={loc.id}
              position={[loc.lat, loc.lng]}
              icon={loc.icon}
              eventHandlers={{
                click: () => {
                  if (isMobile) {
                    setSelectedStation({
                      station_id: loc.id,
                      customer_id: loc.customer_id,
                      id: loc.id,
                      name: loc.name,
                      status: loc.status,
                      ...stats,
                    });
                  }
                },
              }}
            >
              {" "}
              {!isMobile && !errorMessage && (
                <Popup
                  ref={popupRef}
                  closeButton
                  className="station-map-popup"
                  minWidth={300}
                >
                  <div className="station-popup-card">
                    <div className="station-popup-header">
                      <span className="station-popup-icon">
                        <MapPin size={20} />
                      </span>
                      <div>
                        <span
                          className={`station-status ${
                            loc.status === "ONLINE" ? "is-online" : "is-offline"
                          }`}
                        >
                          {loc.status}
                        </span>
                        <h3>{loc.name}</h3>
                        <p>Station ID: {loc.id}</p>
                      </div>
                    </div>

                    <div className="station-popup-stats">
                      <span>
                        <BatteryCharging size={17} />
                        <strong>{stats.totalDispensors}</strong>
                        Dispensers
                      </span>
                      <span>
                        <PlugZap size={17} />
                        <strong>{stats.totalGuns}</strong>
                        Guns
                      </span>
                    </div>

                    <button
                      type="button"
                      className="station-popup-action"
                      onClick={() =>
                        commitSelection({
                          station_id: loc.id,
                          customer_id: loc.customer_id,
                          name: loc.name,
                          status: loc.status,
                          ...stats,
                        })
                      }
                    >
                      <Zap size={17} />
                      See more details
                    </button>
                  </div>
                </Popup>
              )}
            </Marker>
          );
        })}
      </MapContainer>
      {isMobile && !errorMessage && selectedStation && (
        <div className="station-sheet-wrap" role="dialog" aria-modal="true">
          <button
            type="button"
            className="station-sheet-backdrop"
            aria-label="Close station details"
            onClick={() => setSelectedStation(null)}
          />

          <div className="station-sheet">
            <div className="station-sheet-grip" />

            <div className="station-sheet-header">
              <span className="station-popup-icon">
                <MapPin size={20} />
              </span>
              <div>
                <span
                  className={`station-status ${
                    selectedStation.status === "ONLINE"
                      ? "is-online"
                      : "is-offline"
                  }`}
                >
                  {selectedStation.status}
                </span>
                <h3>{selectedStation.name}</h3>
                <p>Station ID: {selectedStation.id}</p>
              </div>
            </div>

            <button
              type="button"
              className="station-sheet-close"
              aria-label="Close station details"
              onClick={() => setSelectedStation(null)}
            >
              <X size={18} />
            </button>

            <div className="station-popup-stats">
              <span>
                <BatteryCharging size={17} />
                <strong>{selectedStation.totalDispensors}</strong>
                Dispensers
              </span>
              <span>
                <PlugZap size={17} />
                <strong>{selectedStation.totalGuns}</strong>
                Guns
              </span>
            </div>

            <button
              type="button"
              className="station-popup-action"
              onClick={() => commitSelection(selectedStation)}
            >
              <Zap size={17} />
              See more details
            </button>
          </div>
        </div>
      )}
      {errorMessage && (
        <div className="fixed inset-0 z-[9100] flex items-center justify-center bg-black/40">
          <div className="w-[320px] rounded-lg bg-white p-6 text-center shadow-lg">
            <p className="text-gray-800">{errorMessage}</p>

            <button
              className="mt-4 rounded bg-blue-600 px-4 py-2 text-white"
              onClick={() => {
                setErrorMessage("");
              }}
            >
              OK
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default MapView;
